import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useCustomPreferenceOrDefault } from '../utils/api';
import { CustomPreference } from '../utils/api/CustomPreference';
import { useSDDStore } from './useSDDStore';
import type { Store } from '../bff-api/stores/model';
import type { HookProps } from './model/HookProps';

export const useSameDayDelivery = (
    props?: HookProps,
): {
    description: string;
    isNextDayDelivery: boolean;
    isLoadingSddStore: boolean;
    isFetchingSddStore: boolean;
    sddStore: Store | undefined;
} => {
    const cutOffTime = useCustomPreferenceOrDefault(
        CustomPreference.GO_LOCAL_CUT_OFF_TIME,
        '14:30',
    );
    const intl = useIntl();
    const {
        data: sddStore,
        isFetching: isFetchingSddStore,
        isLoading: isLoadingSddStore,
    } = useSDDStore(props);

    return useMemo(() => {
        const isNextDayDelivery = checkIsNextDayDelivery(cutOffTime, sddStore?.c_timeZone);
        let description: string;

        if (isNextDayDelivery) {
            description = intl.formatMessage({
                id: 'delivery.options.next.day.delivery',
                defaultMessage: 'Next-Day Delivery',
            });
        } else {
            description = intl.formatMessage({
                id: 'delivery.options.same.day.delivery',
                defaultMessage: 'Same-Day Delivery',
            });
        }

        return {
            description,
            isNextDayDelivery,
            isLoadingSddStore,
            isFetchingSddStore,
            sddStore,
        };
    }, [cutOffTime, intl, sddStore, isFetchingSddStore, isLoadingSddStore]);
};

const checkIsNextDayDelivery = (cutOffTime: string, timeZone: string | undefined): boolean => {
    if (!timeZone) {
        return false;
    }

    const time = cutOffTime.split(':');
    const cutOffTimeReference = Number(time[0]) * 100 + Number(time[1]);

    // Set the Store time zone. The new date still have the browser timezone,
    // but the hours and minutes are from the store time zone.
    const now = new Date(new Date().toLocaleString('en-US', { timeZone }));

    const currentTime = now.getHours() * 100 + now.getMinutes();
    // Using > since hours and minutes are zero-based
    // 00:00 Means: it is always next day delivery
    // 23:59 Means: it is always same day delivery
    return currentTime > cutOffTimeReference;
};
